import type { OptionType } from "$components/inputs";
import {
  SelectField as RHFSelectField,
  TextField as RHFTextField,
} from "$components/rhf-fields";
import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps, ReactNode } from "react";
import {
  InvoiceCardSection,
  useInvoiceCardLayout,
} from "../invoice-card-shell";

const EditableRow = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1.25),
}));

const FieldSlot = styled("div")<Theme, { "data-flex": string }>(
  ({ ...props }) => ({
    flex: props["data-flex"],
    minWidth: 0,
  }),
);

const ReadOnlyGrid = styled("div")<Theme, { "data-columns": string }>(
  ({ theme, ...props }) => ({
    display: "grid",
    gridTemplateColumns: props["data-columns"],
    gap: `${theme.spacing(1.5)}px ${theme.spacing(3)}px`,
  }),
);

const ReadOnlyField = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  minWidth: 0,
}));

const ReadOnlyLabel = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
}));

const ReadOnlyValue = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 600,
  fontSize: 13.5,
  color: theme.palette.text.primary,
}));

export type InpatientDetailsFieldNames = {
  ward: string;
  patientNumber: string;
  admissionDate: string;
  dischargeDate: string;
};

export type InpatientDetailsValues = {
  ward: ReactNode;
  patientNumber: ReactNode;
  admissionDate: ReactNode;
  dischargeDate: ReactNode;
};

const EMPTY_VALUE = "—";

export const InpatientDetails: FC<
  (
    | {
        mode: "editable";
        fieldNames: InpatientDetailsFieldNames;
        wardOptions: OptionType[];
        values?: never;
      }
    | {
        mode: "read-only";
        values: InpatientDetailsValues;
        fieldNames?: never;
        wardOptions?: never;
      }
  ) &
    Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({ mode, fieldNames, wardOptions, values, ...restProps }) => {
  const { isCompact } = useInvoiceCardLayout();

  return (
    <InvoiceCardSection
      tone="alt"
      paddingTop={12}
      paddingBottom={14}
      {...restProps}
    >
      {mode === "editable" ? (
        <EditableRow>
          <FieldSlot data-flex={isCompact ? "1 1 100%" : "2 1 220px"}>
            <RHFSelectField
              name={fieldNames.ward}
              label="Ward"
              options={wardOptions}
              required
              fullWidth
              hideHelper
            />
          </FieldSlot>
          <FieldSlot data-flex={isCompact ? "1 1 100%" : "1 1 200px"}>
            <RHFTextField
              name={fieldNames.patientNumber}
              label="Patient number"
              required
              fullWidth
              hideHelper
            />
          </FieldSlot>
          <FieldSlot data-flex={isCompact ? "1 1 100%" : "1 1 175px"}>
            <RHFTextField
              name={fieldNames.admissionDate}
              label="Admission date"
              type="date"
              fullWidth
              hideHelper
              // A MUI v4 outlined type="date" never reports an empty value as
              // filled, so without this the floating label drops onto the
              // browser's own dd/mm/yyyy placeholder.
              InputLabelProps={{ shrink: true }}
            />
          </FieldSlot>
          <FieldSlot data-flex={isCompact ? "1 1 100%" : "1 1 175px"}>
            <RHFTextField
              name={fieldNames.dischargeDate}
              label="Discharge date"
              type="date"
              fullWidth
              hideHelper
              InputLabelProps={{ shrink: true }}
            />
          </FieldSlot>
        </EditableRow>
      ) : (
        <ReadOnlyGrid data-columns={isCompact ? "1fr 1fr" : "repeat(4, auto)"}>
          <ReadOnlyField>
            <ReadOnlyLabel>Ward</ReadOnlyLabel>
            <ReadOnlyValue>{values.ward || EMPTY_VALUE}</ReadOnlyValue>
          </ReadOnlyField>
          <ReadOnlyField>
            <ReadOnlyLabel>Patient number</ReadOnlyLabel>
            <ReadOnlyValue>{values.patientNumber || EMPTY_VALUE}</ReadOnlyValue>
          </ReadOnlyField>
          <ReadOnlyField>
            <ReadOnlyLabel>Admission date</ReadOnlyLabel>
            <ReadOnlyValue>{values.admissionDate || EMPTY_VALUE}</ReadOnlyValue>
          </ReadOnlyField>
          <ReadOnlyField>
            <ReadOnlyLabel>Discharge date</ReadOnlyLabel>
            <ReadOnlyValue>{values.dischargeDate || EMPTY_VALUE}</ReadOnlyValue>
          </ReadOnlyField>
        </ReadOnlyGrid>
      )}
    </InvoiceCardSection>
  );
};
